import {
  FetchLoginResponse,
  sendLoginRequest,
} from '@lmt/unicorn-shared/app/api/auth/auth-client';
import { useEffect, useState } from 'react';
import { TokenData } from './types';
import { UserClaims } from '@lmt/shared-types';
import { getDispatch } from '../../store/types';
import { logout } from '../../store/actions';
/*
 * According to 'Task 34771: Auto logout on Unicorn closing' we need to log the user out when Unicorn is closed.
 * We decided to use browser Session to determine if Unicorn was closed or not.
 *
 * Since this requirement regarding logging user out might change (or get adjusted, e.g. log out depending on some user preference),
 * it doesn't make sense to change the place where access/refresh token is stored.
 *
 * So we just save the following Key in Session storage (when user logs in), and log the user out if this Key isn't present in Session.
 */
export const AuthSessionKey = '_loggedIn';

export async function login(username: string, password: string) {
  const response = await sendLoginRequest(username, password);

  setAuthData(response);
}

export function useIsAuthorized() {
  return useAuth() !== null;
}

const _setAuthFunctions = new Set<(auth: TokenData | null) => void>();
/*
 * this is a local storage key that will store the AuthData structure (containing access_token and refresh_token)
 */
const authDataKey = 'auth_data';

let _authData: TokenData | null = JSON.parse(
  window.sessionStorage.getItem(authDataKey) || 'null',
);
export function getAuthData() {
  return _authData;
}

function setAuthDataVariable(data: TokenData | null) {
  _authData = data;
  _setAuthFunctions.forEach((item) => {
    item(data);
  });
  window.sessionStorage.setItem(authDataKey, JSON.stringify(data));
}

export function logOut() {
  getDispatch()(logout());
  setAuthDataVariable(null);
  _logoutHandler();
}
export function setAuthData(data: FetchLoginResponse) {
  setAuthDataVariable({
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    claims: data.claims,
  });
}

export function useAuth() {
  const [_, setAuth] = useState<TokenData | null>();
  useEffect(() => {
    _setAuthFunctions.add(setAuth);
    return () => {
      _setAuthFunctions.delete(setAuth);
    };
  }, []);
  return _authData;
}
export function useClaims(): UserClaims {
  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
  return (
    useAuth()?.claims ?? {
      id: '',
      name: '',
      nickname: '',
      permissions: {} as any,
      subtenants: [],
      passwordChangeRequired: undefined,
    }
  );
}

let _logoutHandler = () => {
  /* no action by default */
};

export function addLogoutHandler(handler: () => void) {
  const oldLogoutHandler = _logoutHandler;
  _logoutHandler = () => {
    oldLogoutHandler();
    handler();
  };
}
